import { Observable } from 'rxjs/Rx'
import { Subject } from 'rxjs/Subject'

import { BackendStrategy } from './backends'
import { BaseModel } from '../models'
import { ModelFactory } from '../data/factories'

interface IDelayStoreData {
  model: BaseModel, request: Observable<BaseModel>, status: string
}

interface IDelayStore {
  string: {status: string, data: IDelayStoreData[]}
}

interface IReplayStoreData {
  model: BaseModel, request: Observable<BaseModel>
}

interface IReplayStore {
  string: IReplayStoreData[]
}

export class ModelRepository {

  private static STATUS_IN_PROGRESS = 'in progress'
  private static PERSISTED_MODEL_ATTRIBUTE = '_persisted'

  private static BULK_COLLECTION_PERIOD_IN_MS = 100
  // Stores request which are meant to work in a 'bulk save' manner
  // Multiple Request of the same type, within a timeframe,
  // will be bundle together in a single request
  // TODO [GitHub Issue]: create a small private class (Store) inside this class to abstract _saveDelayStore and _saveReplay
  private _saveDelayStore: IDelayStore | {}
  private _deleteDelayStore: IDelayStore | {}

  // Models that need to be resaved because their data changed while their request was in progress
  private _saveReplay: IReplayStore | {}

  constructor(
    private _repositoryStrategy: BackendStrategy<BaseModel>,
    private _modelFactory?: ModelFactory
  ) {
    this._saveDelayStore = {}
    this._deleteDelayStore = {}
    this._saveReplay = {}
    // Optional modelFactory to not break interface/existing instantiation with ProvidersBuilder
    // Should become mandatory argument in future
  }

  public findModelById(type: string, id: string, includeRelations: string[] = []): Observable<BaseModel> {
    type = this._nonEmptyTypeName(type)
    return this._repositoryStrategy.findById(type, id, includeRelations)
  }

  public findAllModels(type: string, includeRelations: string[] = []): Observable<BaseModel[]> {
    return this.findModelsBy(type, {}, includeRelations) as Observable<BaseModel[]>
  }

  public findModelsBy(
    type: string,
    searchCriteria: any,
    includeRelations: string[] = []
  ): Observable<BaseModel|BaseModel[]> {
    type = this._nonEmptyTypeName(type)
    return this._repositoryStrategy.findBy(type, searchCriteria, includeRelations)
      .map((models) => Array.isArray(models) ? models : [models])
  }

  public createModel(type: string, properties: {[key: string]: any}) {
    if (!this._modelFactory) {
      // Optional modelFactory to not break interface/existing instantiation with ProvidersBuilder
      // Should become mandatory argument in future
      throw new Error(`No ModelFactory provided!`)
    }

    return properties === null
           ? this._modelFactory.getNullObject(type)
           : this._modelFactory.createAndInitialize(type, properties)
  }

  public save(model: BaseModel): Observable<BaseModel> {
    const type = this._nonEmptyTypeName(model.type)

    if (!this._saveDelayStore[type]) {
      this._saveDelayStore[type] = {data: []}
      setTimeout(() => {
        this._saveBackendRequestByType(type)
      }, ModelRepository.BULK_COLLECTION_PERIOD_IN_MS)
    }

    const receiverObservable: Observable<BaseModel> = new Subject<BaseModel>()
    const observableModel = {model: model, request: receiverObservable}
    this._saveReplay[type] = this._saveReplay[type] || []

    if (this._saveDelayStore[type].status === ModelRepository.STATUS_IN_PROGRESS) {
      if (! this._saveReplay[type].some((_observableModel) => _observableModel.model === model)) {
        this._saveReplay[type].push(observableModel)
      }
    } else {
      if (! this._saveDelayStore[type].data.some((_observableModel) => _observableModel.model === model)) {
        this._saveDelayStore[type].data.push(observableModel)
      }
    }

    return receiverObservable
  }

  /**
   * Deletes an object from the backend if it exists/detaches it from it's BaseModel#isPersisted() state
   *
   * @return Observable of the model that has been passed in. After the successful execution, is not stored in the backend
   *  anymore and the returned model will return false for its BaseModel#isPersisted() method
   */
  public delete(model: BaseModel): Observable<boolean> {
    if (ModelRepository.PERSISTED_MODEL_ATTRIBUTE in model) {
      delete model[ModelRepository.PERSISTED_MODEL_ATTRIBUTE]
    } else if (! (model as BaseModel).id) {
      // TODO [GitHub Issue]: Figure out what to do when deleting an object that is being created/saved.
      return Observable.of(true)
    }

    if (! this._deleteDelayStore[model.type]) {
      this._deleteDelayStore[model.type] = {data: []}
      setTimeout(() => {
        this._createDeleteRequest(model.type)
      }, ModelRepository.BULK_COLLECTION_PERIOD_IN_MS)
    }

    const receiverObservable: Observable<boolean> = new Subject<boolean>()
    const observableModel = {model: model, request: receiverObservable}

    if (! this._deleteDelayStore[model.type].data.some((_observableModel) => _observableModel.model === model)) {
      this._deleteDelayStore[model.type].data.push(observableModel)
    }

    return receiverObservable.take(1)
  }

  private _createDeleteRequest(type: string)  {
    if (
      ! this._deleteDelayStore[type]
      || (Array.isArray(this._deleteDelayStore[type].data)
      && this._deleteDelayStore[type].data.length === 0)
    ) {
      throw new Error(`No models of type ${type} exists in the Delete Delay Store`)
    }

    const models = this._deleteDelayStore[type].data.map((observableModel) => observableModel.model)

    return this._repositoryStrategy.delete(models)
      .map((_response) => {
        models.forEach((model, index) => {

          delete model['id']

          // notify the specific model observable of the change
          this._deleteDelayStore[type].data[index].request.next(true)
          this._deleteDelayStore[type].data[index].request.complete()
        })

        delete this._deleteDelayStore[type]
      })
      .subscribe()
  }

  private _nonEmptyTypeName(type: string): string {
    const validatedTypeName = (typeof type === 'string' ? type.trim() : '')
    if (validatedTypeName.length === 0) {
      throw new Error(`Provided "${type}" must be a non-empty string!`)
    }
    return validatedTypeName
  }

  private _saveBackendRequestByType(type: string) {
    if (
      ! this._saveDelayStore[type]
      || (Array.isArray(this._saveDelayStore[type].data)
      && this._saveDelayStore[type].data.length === 0)
    ) {
      throw new Error(`No models of type ${type} exists in the Save Delay Store`)
    }
    const models = this._saveDelayStore[type].data.map((observableModel) => observableModel.model)

    this._saveDelayStore[type].status = ModelRepository.STATUS_IN_PROGRESS

    this._repositoryStrategy.save(models)
      .map((serverModels) => {
        serverModels.forEach((model, index) => {
          const clientModel = models[index]
          clientModel.id = model.id
          clientModel[ModelRepository.PERSISTED_MODEL_ATTRIBUTE] = true
          // notify the specific model observable of the change
          model[ModelRepository.PERSISTED_MODEL_ATTRIBUTE] = true
          this._saveDelayStore[type].data[index].request.next(model)
          this._saveDelayStore[type].data[index].request.complete()
        })
        // Removed all models that have been processed
        delete this._saveDelayStore[type]

        // Process models that need to be resaved
        if (Array.isArray(this._saveReplay[type]) && this._saveReplay[type].length) {
          // Set the data that needs to be replayed
          this._saveDelayStore[type] = {data : this._saveReplay[type]}
          // Removed all models that will be replayed
          delete this._saveReplay[type]
          this._saveBackendRequestByType(type)
        }
      })
      .subscribe((_ignore) => {
        // This fixes an Angular issue where if we do not pass in an OnNext function and multiple request are firing at the
        // same time, angular would cancel any request after the first finishes
      })
  }

}



// WEBPACK FOOTER //
// ./node_modules/@mprep/shared/lib/model-repository/model-repository.ts